import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, useSelector, useDispatch } from 'react-redux';
import cN from 'classnames';
import parse from 'html-react-parser';
import sanitize from 'sanitize-html';
import dayjs from 'dayjs';
import '@magaele/ic-ln/css.css';
import 'normalize.css';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import i18n from '@utils/i18n';
import { homeDomain } from '@utils/domainForCompany';
import { RootState, store } from '@Redux/print/store';
import {
    SubTitle,
    Table,
    ItineraryDaily,
    ItineraryNoticesFrame,
    ItinerarySelfGuided,
} from '@components/DetailPage';
import DetailList from '@components/DetailPage/GroupDetailPrice/subComponents/DetailList';
import PriceList from '@components/DetailPage/GroupDetailPrice/subComponents/PriceList';
import TaxList from '@components/DetailPage/GroupDetailPrice/subComponents/TaxList';
import GroupList from '@components/DetailPage/GroupTripInfoFlight/subComponents/GroupList';
import FeatureContent from '@components/Common/FeatureContent';
import { setPricesInfo, setStationInfo, setFlightInfo, setGroup } from '@Redux/print/Group/actions';
import { setItineraries, setNoticeinfo, setOptionalInfo } from '@Redux/print/Itineraries/actions';
import { setPageType } from '@Redux/common/Page/actions';
import { I_GroupInfo, I_StationInfoList } from '@Redux/print/Group/interfaces';
import { I_OptionalInfo } from '@Redux/print/Itineraries/interfaces';
import { ICollapseListProps } from '@lion-design/core';
import { changeDetailHistory } from '@utils/utils';
import './styles.scss';

i18n();

//列印區塊控制
const getUrlString = new URL(location.href).searchParams;
const PrintItem: string = getUrlString.get('PrintItem') || '';
const PrintItemAry: string[] = PrintItem.split(',');
const hasALL: boolean = PrintItemAry.indexOf('ALL') !== -1; // 全部列印
const isPrintItem = (item: string): boolean => hasALL || PrintItemAry.indexOf(item) !== -1; // 確認是否列印
const isOTP = window.location.host.split('.')[0].indexOf('www') !== -1; // 使用 OTP 登入；false 表示為 agent

const Print = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setPageType('print'));
    }, [dispatch]);

    return (
        <div className={cN('print', 'Container')}>
            <Group />
            <Itinerary />
        </div>
    );
};

function PrintHead(props: I_GroupInfo) {
    const propsData = props.GroupInfo;
    const curTime = dayjs(new Date()).format('YYYY/MM/DD HH:mm');
    const { UpDateTime } = useSelector((store: RootState) => store.itinerariesReducer);
    const formatUpDateTime = dayjs(UpDateTime).format('YYYY/MM/DD HH:mm');
    const { StartFromCityList } = propsData;
    const [imgSrcIsError, setImgSrcIsError] = useState(false);

    interface I_bbcInfo {
        AgentCode: null | string;
        AgentDName: null | string;
        AgentName: null | string;
        AgentWeb: null | string;
        AgentProdList: null | string;
        AgentLogo: null | string;
        AgentFooterDesc: null | string;
        AgentFooterDesc2: null | string;
        AgentAddress: null | string;
        AgentEmail: null | string;
        AgentTel: null | string;
        AgentFax: null | string;
        AgentTqaaid: null | string;
        AgentTbid: null | string;
        AgentSocialmediaid: null | string;
    }
    const [bbcInfo, setBbcInfo] = useState<I_bbcInfo>({
        AgentCode: null,
        AgentDName: null,
        AgentName: null,
        AgentWeb: null,
        AgentProdList: null,
        AgentLogo: null,
        AgentFooterDesc: null,
        AgentFooterDesc2: null,
        AgentAddress: null,
        AgentEmail: null,
        AgentTel: null,
        AgentFax: null,
        AgentTqaaid: null,
        AgentTbid: null,
        AgentSocialmediaid: null,
    });
    useEffect(() => {
        fetch('/common/bbcinfo')
            .then((d) => d.json())
            .then((d) => {
                setBbcInfo((prev) => ({ ...prev, ...d }));
            });
    }, []);

    return (
        <div className="head">
            <img
                className="logo"
                src={bbcInfo.AgentLogo ? '/AgentData/' + bbcInfo.AgentLogo : ''}
                alt={bbcInfo.AgentName ? bbcInfo.AgentName : ''}
            />
            <div className="head_content">
                {!isOTP && !imgSrcIsError && propsData.NormGroupImg ? (
                    <div className="head_img">
                        <img
                            src={propsData.NormGroupImg}
                            onError={() => setImgSrcIsError(true)}
                            alt={propsData.TourName}
                        />
                    </div>
                ) : (
                    ''
                )}
                <div className="head_right">
                    <div className="head_right_top">
                        <span className="head_title">{propsData.TourName}</span>
                        <div className="btn-primary" onClick={() => window.print()}>
                            列印
                        </div>
                    </div>
                    <div className="head_right_bottom">
                        {propsData.TourBuName && (
                            <div className="head_info">聯營商品：{propsData.TourBuName}</div>
                        )}
                        <div className={cN('head_info', 'times')}>
                            <div>團號：{propsData.GroupID}</div>
                            <div className="time">
                                行程更新：<span>{formatUpDateTime}</span>
                            </div>
                        </div>
                        <div className={cN('head_info', 'times')}>
                            <div>
                                出發日期：{propsData.GoDate}(
                                {dayjs(propsData.GoDate).locale('zh-tw').format('dd')})～
                                {propsData.BackDate}(
                                {dayjs(propsData.BackDate).locale('zh-tw').format('dd')}) 共
                                {propsData.TourDays}天
                            </div>
                            <div className="time">
                                列印時間：<span>{curTime}</span>
                            </div>
                        </div>

                        <div className="whereToGo">
                            <span>出發地點：</span>
                            {StartFromCityList && StartFromCityList.length > 0 ? (
                                <span>
                                    {
                                        StartFromCityList.reduce(
                                            (pre, cur) =>
                                                pre.CityName
                                                    ? {
                                                          CityName: `${pre.CityName}、${cur.CityName}`,
                                                      }
                                                    : { CityName: `${cur.CityName}` },
                                            { CityName: '' }
                                        ).CityName
                                    }
                                </span>
                            ) : (
                                <span>－－</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Group() {
    const dispatch = useDispatch();

    const { travelinfo } = document.getElementById('detailPagePara')!.dataset!;
    const { GroupInfo } = JSON.parse(travelinfo!);

    const currency = GroupInfo.CurrencyCode ? `（以 ${GroupInfo.CurrencyCode} 計價）` : '';

    // 從後端提供的 div 提供的資料預先載入
    useEffect(() => {
        dispatch(setGroup(JSON.parse(travelinfo!)));
    }, [dispatch, travelinfo]);

    useEffect(() => {
        const pricesReqBody = {
            GroupID: GroupInfo.GroupID,
        };
        dispatch(setPricesInfo(pricesReqBody)); // 團費說明
    }, [GroupInfo.GroupID, dispatch]);

    useEffect(() => {
        const flightInfoReqBody = {
            NormGroupID: GroupInfo.NormGroupID,
            GroupID: GroupInfo.GroupID,
            GoDate: dayjs(GroupInfo.GoDate).format('YYYY-MM-DD'),
        };
        dispatch(setFlightInfo(flightInfoReqBody)); // 航班詳情
    }, [GroupInfo.GoDate, GroupInfo.GroupID, GroupInfo.NormGroupID, dispatch]);

    //調整網址列參數
    changeDetailHistory({ NormGroupID: GroupInfo.NormGroupID, GroupID: GroupInfo.GroupID });

    const { FlightInfo, PricesInfo } = useSelector((state: RootState) => state.groupReducer);

    return (
        <>
            <PrintHead GroupInfo={GroupInfo} />
            {FlightInfo &&
                FlightInfo.GroupList.length > 0 &&
                FlightInfo.GroupList[0].DirectionList.length > 0 && (
                    <div className="flight">
                        <div className="area_title">
                            <span>參考航班</span>
                            <span>航班時間僅為參考，最終確定之使用航班，以說明會資料為準</span>
                        </div>
                        <GroupList GroupList={FlightInfo.GroupList} />
                    </div>
                )}
            {PricesInfo && PricesInfo.DirectPrice.length > 0 && (
                <div className="price">
                    <div className="area_title">
                        <span>團費說明 {currency}</span>
                    </div>
                    <DetailList />
                    <PriceList />
                    <TaxList />
                </div>
            )}
        </>
    );
}

function Itinerary() {
    const dispatch = useDispatch();
    const { StationInfoList } = useSelector((store: RootState) => store.groupReducer);
    const {
        Features,
        OptionalInfoList,
        Remark,
        LeaveTeamReg,
        SafeReg,
        ActiveNote,
        EachTripList,
        NoteList,
        DailyList,
        AntiepReg,
        ItinnotesReg,
    } = useSelector((store: RootState) => store.itinerariesReducer);
    const isPc = useMediaQuery({ query: '(min-width: 980px)' });

    const { travelinfo } = document.getElementById('detailPagePara')!.dataset!;
    const { IsPreview, GroupInfo } = JSON.parse(travelinfo!);

    useEffect(() => {
        const itinerariesReqbody = {
            TourID: GroupInfo.TourID,
            IsPreview: IsPreview,
            TravelType: GroupInfo.TravelType,
        };
        dispatch(setItineraries(itinerariesReqbody));
    }, [GroupInfo.TourID, GroupInfo.TravelType, IsPreview, dispatch]);

    useEffect(() => {
        const reqBody = {
            GroupID: GroupInfo.GroupID,
        };
        dispatch(setOptionalInfo(reqBody));
        dispatch(setNoticeinfo(reqBody));
        dispatch(setStationInfo(reqBody)); // 集合地點
    }, [GroupInfo.GroupID, dispatch]);

    interface I_Table {
        title: string;
        content: string;
    }
    const TableContent: React.FC<I_Table> = ({ title, content }) => {
        return (
            <>
                <div className="title">{title}</div>
                {content ? (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: sanitize(content, {
                                allowedTags: false,
                                allowedAttributes: false,
                            }),
                        }}
                    />
                ) : (
                    <div>--</div>
                )}
            </>
        );
    };
    //集合地點
    const placeTitles: ICollapseListProps['titles'] = [
        {
            name: '集合地點',
            key: 'Place',
            size: '33.333%',
        },
        {
            name: '地址',
            key: 'Address',
            size: '33.333%',
        },
        {
            name: '集合時間',
            key: 'MusterTime',
            size: '33.333%',
        },
    ];

    const placeData = StationInfoList.map((StationInfoList: I_StationInfoList) => {
        const { Place, MusterTime, Address } = StationInfoList;
        return {
            Place: <TableContent title="集合地點" content={Place} />,
            MusterTime: <TableContent title="集合時間" content={MusterTime} />,
            Address: <TableContent title="地址" content={Address} />,
        };
    });

    //自費行程
    const activityTitles: ICollapseListProps['titles'] = [
        {
            name: '城市',
            key: 'CityName',
            size: '13%',
        },
        {
            name: '節目名稱',
            key: 'ProgramName',
            size: '13%',
        },
        {
            name: '簡介',
            key: 'Summary',
            size: '18%',
        },
        {
            name: '費用',
            key: 'Cost',
            size: '13%',
        },
        {
            name: '年齡說明',
            key: 'AgeDesc',
            size: '9%',
        },
        {
            name: '備註',
            key: 'Remarks',
            size: '27%',
        },
        {
            name: '危險',
            key: 'DangerLevel',
            size: '7%',
        },
    ];
    const activityData = OptionalInfoList.map((OptionalInfoList: I_OptionalInfo) => {
        const {
            CityName,
            ProgramName,
            Summary,
            CurrName,
            CurrID,
            AdultsCost,
            ChildCost,
            AgeDesc,
            Remarks,
            DangerLevel,
        } = OptionalInfoList;

        const adultCost = (CurrID || '') + ' ' + AdultsCost.toLocaleString();
        const childCost = (CurrID || '') + ' ' + ChildCost.toLocaleString();

        const Cost = `${CurrName || ''} <div> 大人 ${adultCost} </div>
            ${ChildCost > 0 ? `<div>小孩 ${childCost}</div>` : ''}`;

        return {
            CityName: <TableContent title="城市" content={CityName} />,
            ProgramName: <TableContent title="節目名稱" content={ProgramName} />,
            Summary: <TableContent title="簡介" content={Summary} />,
            Cost: <TableContent title="費用" content={Cost} />,
            AgeDesc: <TableContent title="年齡說明" content={AgeDesc} />,
            Remarks: <TableContent title="備註" content={Remarks} />,
            DangerLevel: <TableContent title="危險" content={DangerLevel} />,
        };
    });

    //行程特殊提醒
    const noticeContent =
        Remark?.length > 0 &&
        parse(sanitize(Remark, { allowedTags: false, allowedAttributes: false }));
    //行程備註
    const itineraryNoteContent = ItinnotesReg?.length > 0 && parse(ItinnotesReg);
    //安全守則
    const safetyContent = SafeReg?.length > 0 && parse(SafeReg);
    //團體航班規定事項
    const leaveTeamContent = LeaveTeamReg?.length > 0 && parse(LeaveTeamReg);
    //活動附註
    const activeNoteContent = ActiveNote?.length > 0 && parse(ActiveNote);
    // 防疫說明
    const antiepRegContent = AntiepReg?.length > 0 && parse(AntiepReg);

    const scrollOffset = isPc ? -70 : -50;

    //旅遊資訊與網址列判斷
    const checkInfo = NoteList.filter((item) => isPrintItem(item.Title));

    return (
        <div className={cN('itinerary_wrapper', 'Container')}>
            {isPrintItem('FEATURE') && Features && (
                <>
                    <SubTitle texts={{ main: '行程特色', sub: 'Itinerary Features' }} />
                    <FeatureContent features={Features} />
                </>
            )}
            {StationInfoList.length > 0 && (
                <div className={cN('itinerary_table', 'place')} id="assemble">
                    <SubTitle texts={{ main: '集合地點', sub: 'Assembling Place' }} />
                    <Table titles={placeTitles} dataSource={placeData} />
                    <div className="itinerary_note">
                        集合時間僅為參考用，實際時間請以當團人員通知時間為準
                    </div>
                </div>
            )}

            {DailyList.length > 0 && (
                <div className="itinerary" id="schedules">
                    <SubTitle texts={{ main: '每日行程', sub: 'Daily Itinerary' }} />
                    <ItineraryDaily />
                </div>
            )}
            {isPrintItem('FEE') && OptionalInfoList.length > 0 && (
                <div className={cN('itinerary_table', 'activity')}>
                    <SubTitle texts={{ main: '自費活動', sub: 'Activities' }} />
                    <Table titles={activityTitles} dataSource={activityData} />
                    <div className="itinerary_note">
                        自費活動項目以實際出發日為主。
                        <span>危險等級： A危險 B一些危險 C安全</span>
                    </div>
                </div>
            )}
            {EachTripList.length > 0 && (
                <div className="itinerary_self">
                    <SubTitle texts={{ main: '產品內容', sub: 'Contents' }} />
                    <ItinerarySelfGuided />
                </div>
            )}
            {isPrintItem('ANT') && !!AntiepReg && (
                <div className="itinerary_notice" id="EpidemicPrevention">
                    <ItineraryNoticesFrame
                        texts={{ main: '防疫規範', sub: 'Epidemic Prevention Norms' }}
                        scrollEleId="EpidemicPrevention"
                        scrollOffset={scrollOffset}
                    >
                        {antiepRegContent}
                    </ItineraryNoticesFrame>
                </div>
            )}

            {!!noticeContent && (
                <div className="itinerary_notice no_collapse" id="notes">
                    <SubTitle texts={{ main: '行程特殊提醒', sub: 'Itinerary Remarks' }} />
                    <div className="content">{noticeContent}</div>
                </div>
            )}
            {isPrintItem('ITIN') && !!itineraryNoteContent && (
                <div className="itinerary_notice" id="itineraryNote">
                    <ItineraryNoticesFrame
                        texts={{ main: '行程備註', sub: 'Itinerary Notes' }}
                        scrollEleId="itineraryNote"
                        scrollOffset={scrollOffset}
                    >
                        {itineraryNoteContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {isPrintItem('OPTN') && !!activeNoteContent && (
                <div className="itinerary_notice" id="activeNote">
                    <ItineraryNoticesFrame
                        texts={{ main: '活動附註', sub: 'Activity Notes' }}
                        scrollEleId="activeNote"
                        scrollOffset={scrollOffset}
                    >
                        {activeNoteContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {isPrintItem('SAFE') && !!safetyContent && (
                <div className="itinerary_notice" id="safety">
                    <ItineraryNoticesFrame
                        texts={{ main: '安全守則', sub: 'Safety Rules' }}
                        scrollEleId="safety"
                        scrollOffset={scrollOffset}
                    >
                        {safetyContent}
                    </ItineraryNoticesFrame>
                </div>
            )}
            {isPrintItem('OFF') && !!leaveTeamContent && (
                <div className="itinerary_notice" id="leaveTeam">
                    <ItineraryNoticesFrame
                        texts={{ main: '團體航班規定事項', sub: 'Group Flight Rules' }}
                        scrollEleId="leaveTeam"
                        scrollOffset={scrollOffset}
                    >
                        {leaveTeamContent}
                    </ItineraryNoticesFrame>
                </div>
            )}

            {checkInfo.length > 0 && (
                <div className="itinerary_infos" id="notices">
                    <SubTitle texts={{ main: '旅遊資訊', sub: 'Travel Information' }} />
                    {checkInfo.map((item) => {
                        return (
                            <div className="itinerary_infos_print" key={item.Title}>
                                <div className="itinerary_infos_title">{item.CTitle}</div>
                                <div dangerouslySetInnerHTML={{ __html: item.Desc }} />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <Provider store={store}>
            <Print />
        </Provider>
    </React.StrictMode>
);

export default Print;
